import type { ChangeEvent } from 'react';
import Checkbox from '@material-ui/core/Checkbox';
import FormControl from '@material-ui/core/FormControl';
import FormControlLabel from '@material-ui/core/FormControlLabel';
import FormHelperText from '@material-ui/core/FormHelperText';
import Link from '@material-ui/core/Link';
import TextField from '@material-ui/core/TextField';
import Typography from '@material-ui/core/Typography';
import { FieldExtensionComponentProps } from '@backstage/plugin-scaffolder-react';

type ExternalDataLoadingValue = {
  required?: boolean;
  details?: string;
};

export const ExternalDataLoadingRequiredField = ({
  onChange,
  rawErrors,
  required,
  formData,
  schema,
}: FieldExtensionComponentProps<ExternalDataLoadingValue>) => {
  const checked = Boolean(formData?.required);
  const details = formData?.details ?? '';
  const hasError = Boolean(rawErrors?.length);

  const handleChange = (_: ChangeEvent<HTMLInputElement>, value: boolean) => {
    onChange({
      required: value,
      details: value ? details : undefined,
    });
  };

  const handleDetailsChange = (event: ChangeEvent<HTMLInputElement>) => {
    onChange({
      required: checked,
      details: event.target.value || undefined,
    });
  };

  return (
    <FormControl
      required={required}
      error={hasError}
      component="fieldset"
      fullWidth
    >
      <FormControlLabel
        control={
          <Checkbox checked={checked} onChange={handleChange} color="primary" />
        }
        label={schema?.title ?? 'External Data Loading Required'}
      />
      <FormHelperText component="div">
        <Typography variant="caption" color="textSecondary">
          Check this box if external connectivity is required to download data
          from existing outside approved locations. For the unblocking process,
          see{' '}
          <Link
            href="http://www.example.com/"
            target="_blank"
            rel="noopener noreferrer"
          >
            External location unblocking process
          </Link>
          .
        </Typography>
      </FormHelperText>

      <TextField
        fullWidth
        multiline
        minRows={4}
        margin="normal"
        label="External Download Locations"
        value={details}
        onChange={handleDetailsChange}
        disabled={!checked}
        helperText="Describe the external download locations in more detail (e.g., target systems, protocols, ports, and any firewall rules needed)."
      />

      {hasError && <FormHelperText>{rawErrors?.join(', ')}</FormHelperText>}
    </FormControl>
  );
};
